import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { ApiService } from '@/_services/api.service';
import { useStreamingMessage } from '../streaming/useStreamingMessage';
import { clearSearchInputAtom } from '../atoms/uiAtoms';
import { clearInterruptAtom } from '../atoms/interruptAtoms';
import { useMessagesCache } from '../queries/useMessagesStore';
import { EMessageRole, type IMessage } from '@/types';
import { AnalyticsService } from '@/_services/analytics.service.client';

interface SendMessageParams {
	conversationId: string;
	prompt: string;
}

/**
 * Mutation hook to send a message in an existing conversation
 *
 * Flow:
 * 1. Clear search input
 * 2. Add optimistic user message to cache (instant display)
 * 3. Start SSE stream — server 'message' event replaces optimistic with real UUID
 * 4. AI response streams via Jotai (streamingMessageAtom)
 * 5. stream_end commits AI message to cache
 */
export const useSendMessage = () => {
	const { startMessageStream } = useStreamingMessage();
	const clearSearchInput = useSetAtom(clearSearchInputAtom);
	const clearInterrupt = useSetAtom(clearInterruptAtom);
	const { appendMessage } = useMessagesCache();

	return useMutation({
		mutationFn: async ({ conversationId, prompt }: SendMessageParams) => {
			// Return params - actual streaming happens in onSuccess
			return { conversationId, prompt };
		},
		onSuccess: ({ conversationId, prompt }) => {
			clearInterrupt();

			// Track query submission
			AnalyticsService.querySubmitted({
				conversationId,
				queryLength: prompt.length,
				queryText: prompt,
				conversationDepth: 0, // Follow-up; exact depth derived from PostHog event sequences
				isFirstQuery: false
			});

			// Clear the search input immediately
			clearSearchInput();

			// Optimistically add user message to cache for instant display
			const optimisticUserMessage: IMessage = {
				id: `optimistic-${Date.now()}`,
				conversationId,
				text: prompt,
				role: EMessageRole.HUMAN,
				createdAt: new Date(),
				updatedAt: new Date(),
				agentActivities: [],
				citations: []
			};
			appendMessage(conversationId, optimisticUserMessage);

			// Start streaming — server 'message' event will replace optimistic with real UUID
			// AI response streams via Jotai, then stream_end commits to cache
			startMessageStream(async (signal) => ApiService.StreamReplyForMessage({ conversationId, prompt, signal }), {
				conversationId,
				skipStreamStart: true
			});
		}
	});
};
